import React, { useState } from 'react';
import { Organization } from '@/types';

interface OrganizationsTabProps {
    organizations: Organization[];
    isDark: boolean;
    setShowCreateOrgModal: (show: boolean) => void;
    onDeleteOrganization: (orgId: number) => Promise<void>;
}

const OrganizationsTab: React.FC<OrganizationsTabProps> = ({
    organizations,
    isDark,
    setShowCreateOrgModal,
    onDeleteOrganization
}) => {
    const [deletingOrgId, setDeletingOrgId] = useState<number | null>(null);
    
    const handleDelete = async (org: Organization) => {
        if (!confirm(`Are you sure you want to delete "${org.name}"? This will also delete all teams and memberships within the organization.`)) {
            return;
        }
        
        try {
            setDeletingOrgId(org.id);
            await onDeleteOrganization(org.id);
        } finally {
            setDeletingOrgId(null);
        }
    };
    
    return (
    <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} backdrop-blur-lg rounded-2xl border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
        <div className={`p-6 border-b ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'}`}>
            <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold cgm-text-primary">Organizations</h3>
                <button
                    onClick={() => setShowCreateOrgModal(true)}
                    className="bg-gradient-to-r from-purple-600 to-purple-700 cgm-text-tertiary px-6 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-purple-800 transition-all duration-200 transform hover:scale-105"
                >
                    Create Organization
                </button>
            </div>
        </div>
        <div className="overflow-x-auto rounded-b-2xl">
            <table className="min-w-full">
                <thead className={`${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold cgm-text-primary">Name</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold cgm-text-primary">Description</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold cgm-text-primary">Created</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold cgm-text-primary">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {organizations.map((org, index) => (
                        <tr key={org.id} className={`${isDark ? 'hover:bg-gray-700/30' : 'hover:bg-gray-100'} transition-colors ${index % 2 === 0 ? '' : isDark ? 'bg-gray-800/25' : 'bg-white'}`}>
                            <td className="px-6 py-4">
                                <div className="text-sm font-medium cgm-text-primary">{org.name}</div>
                            </td>
                            <td className="px-6 py-4">
                                <div className="text-sm cgm-text-secondary">{org.description || 'No description'}</div>
                            </td>
                            <td className="px-6 py-4">
                                <div className="text-sm cgm-text-secondary">
                                    {new Date(org.created_at).toLocaleDateString()}
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                <button
                                    onClick={() => handleDelete(org)}
                                    disabled={deletingOrgId === org.id}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                        deletingOrgId === org.id
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-red-600 hover:bg-red-700 cgm-text-tertiary transform hover:scale-105'
                                    }`}
                                >
                                    {deletingOrgId === org.id ? 'Deleting...' : 'Delete'}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
    );
};

export default OrganizationsTab; 